// @systeme: interface
// Un seul endroit pour dire quelque chose à l'utilisatrice en passant : « C'est noté »,
// « Pas de réseau »… Un message à la fois, en bas, au-dessus de la navigation.
import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react';
import Snackbar from '@mui/material/Snackbar';
import { ErreurClient } from '../lib/api';
import { textes } from '../lib/textes';
import { tailles } from '../theme/theme';

interface ValeurMessages {
  dire(message: string): void;
  /** Transforme n'importe quelle erreur en message humain et l'affiche. */
  direErreur(erreur: unknown): void;
}

const Contexte = createContext<ValeurMessages | undefined>(undefined);

export function messageDe(erreur: unknown): string {
  if (erreur instanceof ErreurClient) return erreur.message;
  return textes.erreurTechnique;
}

export function MessagesProvider({ children }: { children: ReactNode }) {
  const [message, setMessage] = useState<string | undefined>();
  const dire = useCallback((m: string) => setMessage(m), []);
  const direErreur = useCallback((e: unknown) => setMessage(messageDe(e)), []);
  const valeur = useMemo(() => ({ dire, direErreur }), [dire, direErreur]);
  return (
    <Contexte.Provider value={valeur}>
      {children}
      <Snackbar
        open={message !== undefined}
        autoHideDuration={4000}
        onClose={() => setMessage(undefined)}
        message={message}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        sx={{ bottom: { xs: tailles.navigation + 16 } }}
      />
    </Contexte.Provider>
  );
}

export function useMessages(): ValeurMessages {
  const valeur = useContext(Contexte);
  if (!valeur) throw new Error('useMessages doit être utilisé sous MessagesProvider.');
  return valeur;
}
